"use client";

import {JSX, ReactNode, useEffect, useRef, useState} from "react";

import {Typography} from "@/shared/ui/data-display/typography";

import "./styles/collapsible.scss";

export interface CollapsibleProps {
    label: string;
    value?: ReactNode;
    /** Trailing affordance in the trigger row (e.g. a chevron), rendered after the value. */
    icon?: ReactNode;
    isOpen?: boolean;
    onToggle?: () => void;
    children: ReactNode;
    className?: string;
    fullscreen?: boolean;
    error?: boolean;
    onAnimationEnd?: () => void;
}

export function Collapsible({
    label,
    value,
    icon,
    isOpen = false,
    onToggle,
    children,
    className,
    fullscreen = false,
    error = false,
    onAnimationEnd,
}: CollapsibleProps): JSX.Element {
    const openClass = isOpen ? "collapsible--open" : "";
    const errorClass = error ? "collapsible--error" : "";

    const wrapperRef = useRef<HTMLDivElement>(null);
    const collapsibleRef = useRef<HTMLDivElement>(null);
    const capturedHeightRef = useRef<number | null>(null);

    const [isFullscreenActive, setIsFullscreenActive] = useState(false);
    const [wrapperHeight, setWrapperHeight] = useState<number | null>(null);

    useEffect(() => {
        if (fullscreen && isOpen && !isFullscreenActive && wrapperRef.current) {
            const height = wrapperRef.current.offsetHeight;
            capturedHeightRef.current = height;
            setWrapperHeight(height);
            setIsFullscreenActive(true);
            onAnimationEnd?.();
        } else if (!fullscreen && isFullscreenActive) {
            setIsFullscreenActive(false);
            setWrapperHeight(null);
        }
    }, [fullscreen, isOpen, isFullscreenActive, onAnimationEnd]);

    const fullscreenClass = isFullscreenActive ? "collapsible--fullscreen" : "";
    const wrapperClass = isFullscreenActive ? "collapsible-wrapper--fullscreen-active" : "";
    const wrapperStyle = wrapperHeight ? {height: `${wrapperHeight}px`} : {};

    return (
        <div ref={wrapperRef} className={`collapsible-wrapper ${wrapperClass}`} style={wrapperStyle}>
            <div ref={collapsibleRef} className={`collapsible ${openClass} ${fullscreenClass} ${errorClass} ${className || ""}`}>
                <div role="button" className="collapsible__trigger" onClick={onToggle}>
                    <Typography as="span" size={14} weight={500} color={isOpen ? "title" : "paragraph"} className="collapsible__label">
                        {label}
                    </Typography>
                    {value && (
                        <Typography as="span" size={14} weight={600} color="title" className="collapsible__value">
                            {value}
                        </Typography>
                    )}
                    {icon && <span className="collapsible__icon">{icon}</span>}
                </div>
                {isOpen && <div className="collapsible__content">{children}</div>}
            </div>
        </div>
    );
}
